import { useState } from 'react'
import { Button, Checkbox, Modal, Select, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconTrash, IconX } from '@tabler/icons-react'
import { CHART_RECORD_REMOVAL_REASONS } from '@behuman/core-contract'
import {
  CHART_RECORD_REMOVAL_REASON_LABELS,
  type ChartRecordRemovalReason,
} from './chart-record-removal-reasons'

export type { ChartRecordRemovalReason }

// The reader-facing labels, plus the one hint that belongs to the PICKER rather than to
// the record: choosing "Other" is what makes the explanation box mandatory below.
const REASON_OPTIONS = CHART_RECORD_REMOVAL_REASONS.map((value) => ({
  value,
  label:
    value === 'other'
      ? `${CHART_RECORD_REMOVAL_REASON_LABELS[value]} — require explanation`
      : CHART_RECORD_REMOVAL_REASON_LABELS[value],
}))

const CONFIRM_WORD = 'delete'

type FormValues = {
  reason: ChartRecordRemovalReason | ''
  explanation: string
  acknowledged: boolean
}

type Props = {
  opened: boolean
  onClose: () => void
  onConfirm: (values: { reason: ChartRecordRemovalReason; explanation?: string }) => Promise<void>
  recordTypeLabel: string
  createdOnLabel?: string
  loading?: boolean
}

export function TypedDeleteConfirmModal({
  opened,
  onClose,
  onConfirm,
  recordTypeLabel,
  createdOnLabel,
  loading,
}: Props) {
  const [confirmText, setConfirmText] = useState('')
  const form = useForm<FormValues>({
    initialValues: { reason: '', explanation: '', acknowledged: false },
    validate: {
      reason: (value) => (!value ? 'Please select a reason for removal' : null),
      explanation: (value, values) =>
        values.reason === 'other' && !value.trim() ? 'Please explain the reason' : null,
    },
  })

  const blockPaste = (event: { preventDefault: () => void }) => event.preventDefault()

  const handleClose = () => {
    form.reset()
    setConfirmText('')
    onClose()
  }

  const isConfirmTextValid = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const canSubmit = isConfirmTextValid && form.values.acknowledged

  const handleSubmit = async (values: FormValues) => {
    if (!values.reason || !canSubmit) return
    await onConfirm({
      reason: values.reason,
      explanation: values.reason === 'other' ? values.explanation.trim() : undefined,
    })
    handleClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size={460}
      padding={0}
      radius={16}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.24, blur: 2 }}
      styles={{ content: { overflow: 'hidden' }, body: { padding: 0 } }}
    >
      <form onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
        <div className="flex items-start gap-3 border-b border-[#EEF0F2] px-[18px] py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#F3C9C0] bg-[#FDECE8] text-[#E5533D]">
            <IconTrash size={18} stroke={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[18px] leading-6 font-extrabold tracking-tight text-[#1F2733]">
              Remove from patient chart?
            </div>
            <div className="mt-1 text-[12.5px] leading-5 font-medium text-[#6B7280]">
              {recordTypeLabel}
              {createdOnLabel ? ` • ${createdOnLabel}` : ''}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E7E9EC] text-[#9AA0A8] hover:bg-[#F4F6F8]"
          >
            <IconX size={16} stroke={2.2} />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-[18px] py-4">
          <Text size="sm" c="dimmed">
            This item will no longer appear in the active patient chart. It will remain available to
            authorized administrators for audit and compliance purposes.
          </Text>

          <Select
            label="Reason for removal"
            placeholder="Select a reason"
            data={REASON_OPTIONS}
            withAsterisk
            comboboxProps={{ withinPortal: false }}
            {...form.getInputProps('reason')}
          />

          {form.values.reason === 'other' && (
            <Textarea
              label="Explanation"
              placeholder="Explain why this item is being removed"
              autosize
              minRows={2}
              withAsterisk
              {...form.getInputProps('explanation')}
            />
          )}

          <Checkbox
            label="I understand this action will be recorded in the audit log."
            {...form.getInputProps('acknowledged', { type: 'checkbox' })}
          />

          <TextInput
            label={`Type "${CONFIRM_WORD}" to confirm`}
            placeholder={CONFIRM_WORD}
            value={confirmText}
            onChange={(event) => setConfirmText(event.currentTarget.value)}
            onPaste={blockPaste}
            onDrop={blockPaste}
            onContextMenu={blockPaste}
            autoComplete="off"
            error={
              confirmText.length > 0 && !isConfirmTextValid
                ? `Type "${CONFIRM_WORD}" exactly to confirm`
                : null
            }
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-[#EEF0F2] bg-[#FAFBFC] px-[18px] py-3">
          <Button variant="default" type="button" onClick={handleClose} radius="md">
            Cancel
          </Button>
          <Button
            color="red"
            type="submit"
            loading={loading}
            disabled={!canSubmit}
            leftSection={<IconTrash size={16} />}
            radius="md"
          >
            Remove from chart
          </Button>
        </div>
      </form>
    </Modal>
  )
}
